import React, { useState } from 'react';
import { Sparkles, Lock, Loader2 } from 'lucide-react';
import { Place } from '../../../types/results.types';
import InlineDMUnlockModal from '../InlineDMUnlockModal/InlineDMUnlockModal';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { findablePeopleIds } from '../../../utils/rowActions';
import { usePostHog } from 'posthog-js/react';

interface InlineBulkDMUnlockButtonProps {
  place: Place;
  onUpgradeClick?: () => void;
  searchId?: number;
  onSuccess?: () => void;
  onOpenModal?: (personIds: number[]) => void;
  buttonClassName?: string;
  lockedButtonClassName?: string;
  loadingButtonClassName?: string;
}

const DEFAULT_BUTTON_CLASS =
  'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-[#8A3033] transition-all duration-[120ms] ease-out hover:bg-[#F4F4F5] active:scale-[0.98] active:bg-[#E4E4E7]';

const DEFAULT_LOCKED_CLASS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-400 text-white text-xs font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200';

const DEFAULT_LOADING_CLASS =
  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-gray-400 cursor-not-allowed';

const InlineBulkDMUnlockButton: React.FC<InlineBulkDMUnlockButtonProps> = ({
  place,
  onUpgradeClick,
  searchId,
  onSuccess,
  onOpenModal,
  buttonClassName,
  lockedButtonClassName,
  loadingButtonClassName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPersonIds, setModalPersonIds] = useState<number[]>([]);
  const { hasDMUnlockAccess } = useFeatureAccess();
  const posthog = usePostHog();

  const personIds = findablePeopleIds(place);

  const isPollingForThisRow = useAppSelector((state) => {
    const batches = Object.values(state.dmUnlock.activeBatches);
    if (batches.length === 0) return false;
    return batches.some((b) => b.personIds.some((id) => personIds.includes(id)));
  });

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalPersonIds([]);
  };

  const handleOpenModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    posthog?.capture('enrich_people_clicked', {
      context: 'row',
      search_id: searchId,
      person_count: personIds.length,
      has_access: true,
    });
    if (onOpenModal) {
      onOpenModal(personIds);
      return;
    }
    setModalPersonIds(personIds);
    setIsModalOpen(true);
  };

  const discoveryModal =
    !onOpenModal && isModalOpen && modalPersonIds.length > 0 ? (
      <InlineDMUnlockModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        personIds={modalPersonIds}
        searchId={searchId}
        onSuccess={() => {
          if (onSuccess) {
            onSuccess();
          }
        }}
        onUpgradeClick={onUpgradeClick}
      />
    ) : null;

  if (personIds.length === 0 && !isModalOpen) {
    return null;
  }

  if (!hasDMUnlockAccess) {
    return (
      <button
        onClick={(event) => {
          event.stopPropagation();
          posthog?.capture('locked_feature_clicked', { feature: 'owner_email_discovery' });
          onUpgradeClick?.();
        }}
        className={lockedButtonClassName ?? DEFAULT_LOCKED_CLASS}
        title="Growth plan required to unlock owner email discovery"
      >
        <Lock className="w-3.5 h-3.5" />
        Find decision-maker emails ({personIds.length})
      </button>
    );
  }

  if (isPollingForThisRow) {
    return (
      <>
        <button
          type="button"
          disabled
          aria-busy="true"
          aria-live="polite"
          className={loadingButtonClassName ?? DEFAULT_LOADING_CLASS}
          title="Decision-maker email discovery already in progress"
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          Finding…
        </button>
        {discoveryModal}
      </>
    );
  }

  return (
    <>
      {personIds.length > 0 && (
        <button
          onClick={handleOpenModal}
          className={buttonClassName ?? DEFAULT_BUTTON_CLASS}
          aria-busy="false"
          title={`Find decision-maker emails for ${personIds.length} ${personIds.length > 1 ? 'people' : 'person'} at ${place.name}`}
        >
          <Sparkles className="w-3 h-3" />
          Find decision-maker emails{personIds.length > 1 ? ` (${personIds.length})` : ''}
        </button>
      )}
      {discoveryModal}
    </>
  );
};

export default InlineBulkDMUnlockButton;
